import React, { useEffect, useRef } from "react";

/*
  Ненавязчивая фон-анимация для страницы логина:
  - Частицы-пузырьки и шестиугольники (технологичность) в фирменной сиреневой гамме
  - Очень лёгкая по CPU: ~24 fps, до 60 частиц
  - Без взаимодействия (pointer-events: none)
*/
export default function CleanTechBackground({ opacity = 0.25, position = 'fixed', zIndex = 0, className, style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const particlesRef = useRef([]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');

    const DPR = Math.min(2, window.devicePixelRatio || 1);
    const resize = () => {
      const w = c.clientWidth;
      const h = c.clientHeight;
      c.width = Math.floor(w * DPR);
      c.height = Math.floor(h * DPR);
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    };
    const onResize = () => resize();
    resize();
    window.addEventListener('resize', onResize);

    // Инициализируем частицы (пузырьки + шестиугольники)
    const W = () => c.clientWidth || 1200;
    const H = () => c.clientHeight || 800;
    const palette = [
      '#7b3ae9', // фирменный
      '#ad76fb',
      '#6a49b4',
      '#8a63df',
    ];

    const makeParticle = () => {
      const type = Math.random() < 0.75 ? 'bubble' : 'hex';
      return {
        type,
        x: Math.random() * W(),
        y: Math.random() * H(),
        r: type === 'bubble' ? (6 + Math.random() * 14) : (8 + Math.random() * 16),
        vx: (Math.random() - 0.5) * 0.2,
        vy: (Math.random() - 0.5) * 0.2,
        a: Math.random() * Math.PI * 2,
        va: (Math.random() - 0.5) * 0.002,
        color: palette[Math.floor(Math.random() * palette.length)],
        alpha: 0.25 + Math.random() * 0.35,
      };
    };

    const count = Math.min(60, Math.floor((W() * H()) / 25000));
    particlesRef.current = Array.from({ length: count }, makeParticle);

    let last = 0;
    const loop = (t) => {
      if (t - last < 1000 / 24) { rafRef.current = requestAnimationFrame(loop); return; }
      last = t;
      ctx.clearRect(0, 0, c.width, c.height);

      // мягкий градиентный фон
      const g = ctx.createLinearGradient(0, 0, W(), H());
      g.addColorStop(0, 'rgba(21, 18, 34, 0.8)');
      g.addColorStop(1, 'rgba(13, 12, 20, 0.8)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, W(), H());

      particlesRef.current.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        p.a += p.va;

        if (p.x < -40) p.x = W() + 40; if (p.x > W() + 40) p.x = -40;
        if (p.y < -40) p.y = H() + 40; if (p.y > H() + 40) p.y = -40;

        ctx.save();
        ctx.globalAlpha = p.alpha * opacity;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.a);
        ctx.strokeStyle = p.color;
        ctx.lineWidth = 1.5;
        if (p.type === 'bubble') {
          ctx.beginPath();
          ctx.arc(0, 0, p.r, 0, Math.PI * 2);
          ctx.stroke();
          // бликовая дуга
          ctx.beginPath();
          ctx.arc(-p.r * 0.3, -p.r * 0.3, p.r * 0.6, Math.PI * 1.2, Math.PI * 1.6);
          ctx.stroke();
        } else {
          // шестиугольник
          const R = p.r;
          ctx.beginPath();
          for (let i = 0; i < 6; i++) {
            const ang = (Math.PI / 3) * i;
            const X = Math.cos(ang) * R;
            const Y = Math.sin(ang) * R;
            if (i === 0) ctx.moveTo(X, Y); else ctx.lineTo(X, Y);
          }
          ctx.closePath();
          ctx.stroke();
          // щетинка-«штрих» для клининговой ассоциации
          ctx.beginPath();
          ctx.moveTo(0, -R * 0.7);
          ctx.lineTo(0, -R * 1.2);
          ctx.stroke();
        }
        ctx.restore();
      });

      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
    };
  }, [opacity]);

  return (
    <div
      className={className}
      style={{ position, inset: 0, zIndex, pointerEvents: 'none', ...(style || {}) }}
      aria-hidden="true"
    >
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
}
